import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiDollarSign, FiCalculator } = FiIcons;

const MathSection = () => {
  return (
    <section className="math-section py-20 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            How <span className="text-green-600">₹1 Crore+/Year</span> Is Possible
          </h2>
          <p className="text-xl text-gray-600">(Simple Math)</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
          <motion.div 
            className="math-breakdown"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-gray-50 p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <SafeIcon icon={FiCalculator} className="mr-3 text-blue-600" />
                The Math Breakdown
              </h3>
              
              <div className="space-y-4 text-lg">
                <div className="math-step bg-white p-4 rounded-lg border-l-4 border-blue-600">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Sign 2 clients per week at ₹5,000/month each</span>
                    <span className="font-bold text-blue-600">₹10,000/week</span>
                  </div>
                </div>
                
                <div className="math-step bg-white p-4 rounded-lg border-l-4 border-green-600">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Month 1 recurring revenue</span>
                    <span className="font-bold text-green-600">₹40,000/month</span>
                  </div>
                </div>
                
                <div className="math-step bg-white p-4 rounded-lg border-l-4 border-purple-600">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">By Month 12 recurring revenue</span>
                    <span className="font-bold text-purple-600">₹5.2 Lakhs/month</span>
                  </div>
                </div>
                
                <div className="math-step bg-gradient-to-r from-yellow-400 to-yellow-600 p-4 rounded-lg text-black">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">First-year cumulative revenue</span>
                    <span className="font-bold text-2xl">₹1.37 Crore</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="visual-chart"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-blue-50 to-green-50 p-8 rounded-2xl">
              <h4 className="text-xl font-bold text-gray-900 mb-6 text-center">Monthly Recurring Revenue Growth</h4>
              <div className="space-y-3">
                {[
                  { month: 'Month 1', amount: '₹40,000', width: '8%' },
                  { month: 'Month 3', amount: '₹1.2L', width: '24%' },
                  { month: 'Month 6', amount: '₹2.4L', width: '48%' },
                  { month: 'Month 9', amount: '₹3.6L', width: '72%' },
                  { month: 'Month 12', amount: '₹5.2L', width: '100%' }
                ].map((item, index) => (
                  <motion.div 
                    key={item.month} 
                    className="flex items-center space-x-4"
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <span className="w-20 text-sm text-gray-600">{item.month}</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-6 relative overflow-hidden">
                      <motion.div 
                        className="bg-gradient-to-r from-blue-500 to-green-500 h-full rounded-full flex items-center justify-end pr-2"
                        initial={{ width: '0%' }}
                        whileInView={{ width: item.width }}
                        transition={{ duration: 1, delay: index * 0.2 }}
                        viewport={{ once: true }}
                      >
                        <span className="text-white font-semibold text-sm">{item.amount}</span>
                      </motion.div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div 
          className="highlight-box bg-gradient-to-r from-green-100 to-blue-100 p-8 rounded-2xl border-2 border-green-300 mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="text-center">
            <SafeIcon icon={FiTrendingUp} className="text-4xl text-green-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-4">This is just with ₹5,000/month per client.</h3>
            <p className="text-xl text-gray-700">
              Many clients will happily pay <span className="font-bold text-green-600">₹10,000–₹25,000/month</span> when combined with agency services.
            </p>
          </div>
        </motion.div>

        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <motion.a
            href="https://abdul.cc/earnwithabdul"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-gradient-to-r from-green-600 to-green-800 text-white font-bold text-xl px-12 py-6 rounded-full hover:from-green-700 hover:to-green-900 transition-all duration-300 transform hover:scale-105 shadow-2xl"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            I Want This Kind of Recurring Income – Apply Now
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default MathSection;